import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { tenantStorage } from 'utils/tenantStorage';
import {
  clearPersistedRefreshTokens,
  sessionRefreshStore,
} from 'utils/sessionRefreshStore';
import { isAccessJwt } from 'utils/jwtClaims';
import {
  AuthTokens,
  B2bAccessRequestPayload,
  CustomerProfile,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
} from 'types/auth';

export interface StoreTokenApiResponse {
  accessToken?: string;
  AccessToken?: string;
  refreshToken?: string;
  RefreshToken?: string;
  customerId?: number;
  CustomerId?: number;
  expiresAt?: string;
  ExpiresAt?: string;
  priceListId?: string;
  PriceListId?: string;
  customerType?: string;
  CustomerType?: string;
  tenantCode?: string;
  TenantCode?: string;
  csrfToken?: string;
  CsrfToken?: string;
}

interface StoreProfileApiResponse {
  customerId?: number;
  CustomerId?: number;
  name?: string;
  Name?: string;
  email?: string;
  Email?: string;
  phone?: string;
  Phone?: string;
  customerType?: string;
  CustomerType?: string;
  priceListId?: string;
  PriceListId?: string;
  priceListName?: string;
  PriceListName?: string;
  zoneId?: string;
  ZoneId?: string;
  sellerId?: string;
  SellerId?: string;
  storeUserId?: number;
  StoreUserId?: number;
  userEmail?: string;
  UserEmail?: string;
  userDisplayName?: string;
  UserDisplayName?: string;
  cuit?: string;
  Cuit?: string;
  porcDesc?: number;
  PorcDesc?: number;
  porcDesc2?: number;
  PorcDesc2?: number;
  porcDesc3?: number;
  PorcDesc3?: number;
}

export const mapTokens = (data: StoreTokenApiResponse, emailHint?: string): AuthTokens => {
  const rawToken = data.accessToken || data.AccessToken || '';
  const rawRefresh = data.refreshToken || data.RefreshToken || '';
  const customerId = String(data.customerId ?? data.CustomerId ?? '');
  const csrfToken = (data.csrfToken || data.CsrfToken || '').trim();
  return {
    token: isAccessJwt(rawToken) ? rawToken : '',
    refreshToken: isAccessJwt(rawRefresh) ? rawRefresh : '',
    customerId,
    email: emailHint || '',
    displayName: '',
    csrfToken: csrfToken || undefined,
  };
};

const hasCookieSession = (): boolean =>
  tenantStorage.getItem(STORAGE_KEYS.CUSTOMER_COOKIE_SESSION) === '1';

const persistSession = (data: AuthTokens) => {
  clearPersistedRefreshTokens();
  if (isAccessJwt(data.token)) {
    tenantStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
    tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_COOKIE_SESSION);
    api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
  } else {
    tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
    tenantStorage.setItem(STORAGE_KEYS.CUSTOMER_COOKIE_SESSION, '1');
    delete api.defaults.headers.common.Authorization;
  }
  if (data.refreshToken) {
    sessionRefreshStore.set(data.refreshToken);
  } else {
    sessionRefreshStore.clear();
  }
  if (data.customerId) {
    tenantStorage.setItem(STORAGE_KEYS.CUSTOMER_ID, data.customerId);
  }
  if (data.email) {
    tenantStorage.setItem(STORAGE_KEYS.EMAIL, data.email);
  }
  if (data.displayName) {
    tenantStorage.setItem(STORAGE_KEYS.DISPLAY_NAME, data.displayName);
  }
  if (data.csrfToken) {
    tenantStorage.setItem(STORAGE_KEYS.CUSTOMER_CSRF, data.csrfToken);
    tenantStorage.setItem(STORAGE_KEYS.ADMIN_CSRF, data.csrfToken);
  }
};

const clearSessionStorage = () => {
  tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
  tenantStorage.removeItem(STORAGE_KEYS.EMAIL);
  tenantStorage.removeItem(STORAGE_KEYS.DISPLAY_NAME);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_COOKIE_SESSION);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_CSRF);
  sessionRefreshStore.clear();
  clearPersistedRefreshTokens();
  delete api.defaults.headers.common.Authorization;
};

const mapProfile = (data: StoreProfileApiResponse): CustomerProfile => {
  const name = (data.userDisplayName || data.UserDisplayName || data.name || data.Name || '').trim();
  const parts = name.split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  const customerTypeRaw = (data.customerType || data.CustomerType || '').toUpperCase();
  // Store es canal mayorista: "C" (cliente ERP) y vacío cuentan como B2B; solo B2C explícito queda B2C.
  const customerType: CustomerProfile['customerType'] =
    customerTypeRaw === 'B2C' || customerTypeRaw === 'CONSUMIDOR' ? 'B2C' : 'B2B';
  const storeUserId = data.storeUserId ?? data.StoreUserId;
  return {
    customerId: String(data.customerId ?? data.CustomerId ?? ''),
    email: data.userEmail || data.UserEmail || data.email || data.Email || '',
    firstName,
    lastName,
    phone: data.phone || data.Phone,
    customerType,
    priceListId: (data.priceListId || data.PriceListId || '').trim() || undefined,
    priceListName: (data.priceListName || data.PriceListName || '').trim() || undefined,
    zoneId: (data.zoneId || data.ZoneId || '').trim() || undefined,
    sellerId: (data.sellerId || data.SellerId || '').trim() || undefined,
    storeUserId: storeUserId != null ? String(storeUserId) : undefined,
    userDisplayName: (data.userDisplayName || data.UserDisplayName || '').trim() || undefined,
    companyName: (data.name || data.Name || '').trim() || undefined,
    cuit: (data.cuit || data.Cuit || '').trim() || undefined,
    porcDesc: clampPercent(data.porcDesc ?? data.PorcDesc),
    porcDesc2: clampPercent(data.porcDesc2 ?? data.PorcDesc2),
    porcDesc3: clampPercent(data.porcDesc3 ?? data.PorcDesc3),
  };
};

const clampPercent = (value: unknown): number => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 100);
};

const authenticationService = {
  hasCookieSession,

  hasSession: (): boolean => {
    const token = tenantStorage.getItem(STORAGE_KEYS.TOKEN);
    if (isAccessJwt(token)) return true;
    if (token) tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
    return hasCookieSession();
  },

  getCsrfToken: (): string | null => tenantStorage.getItem(STORAGE_KEYS.CUSTOMER_CSRF),

  login: async (data: LoginRequest): Promise<AuthTokens> => {
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_LOGIN, data);
    const tokens = mapTokens(response.data, data.email);
    persistSession(tokens);
    return tokens;
  },

  register: async (data: RegisterRequest): Promise<AuthTokens> => {
    const name = `${data.firstName} ${data.lastName}`.trim();
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_REGISTER, {
      email: data.email,
      password: data.password,
      name,
    });
    const tokens = mapTokens(response.data, data.email);
    tokens.displayName = name;
    persistSession(tokens);
    return tokens;
  },

  activateAccount: async (data: ResetPasswordRequest): Promise<AuthTokens> => {
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_ACTIVATE, {
      token: data.token,
      newPassword: data.newPassword,
    });
    const tokens = mapTokens(response.data);
    persistSession(tokens);
    return tokens;
  },

  requestB2bAccess: async (data: B2bAccessRequestPayload): Promise<{ outcome: string; message: string }> => {
    const response = await api.post<{
      outcome?: string;
      Outcome?: string;
      message?: string;
      Message?: string;
    }>(ENDPOINTS.AUTH_B2B_REQUEST, data);
    return {
      outcome: String(response.data.outcome || response.data.Outcome || 'pending'),
      message: String(
        response.data.message ||
          response.data.Message ||
          'Recibimos tu solicitud. Te vamos a contactar.'
      ),
    };
  },

  getB2bAccessRequestStatus: async (
    cuit: string,
    email: string
  ): Promise<{ status: string; message: string }> => {
    const response = await api.get<{
      status?: string;
      Status?: string;
      message?: string;
      Message?: string;
    }>(ENDPOINTS.AUTH_B2B_REQUEST_STATUS, { params: { cuit, email } });
    return {
      status: String(response.data.status || response.data.Status || 'none'),
      message: String(response.data.message || response.data.Message || ''),
    };
  },

  logout: async (): Promise<void> => {
    try {
      const refreshToken = sessionRefreshStore.get();
      await api.post(ENDPOINTS.AUTH_LOGOUT, refreshToken ? { refreshToken } : {});
    } catch {
      // La cookie puede haber vencido; igual se limpia el estado local.
    } finally {
      clearSessionStorage();
    }
  },

  refreshToken: async (refreshToken?: string): Promise<AuthTokens> => {
    const tokenToUse = refreshToken || sessionRefreshStore.get();
    if (!tokenToUse && !hasCookieSession()) {
      throw new Error('No refresh token available');
    }
    const response = await api.post<StoreTokenApiResponse>(
      ENDPOINTS.AUTH_REFRESH,
      tokenToUse ? { refreshToken: tokenToUse } : {},
    );
    const email = tenantStorage.getItem(STORAGE_KEYS.EMAIL) || undefined;
    const tokens = mapTokens(response.data, email);
    persistSession(tokens);
    return tokens;
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    await api.post(ENDPOINTS.AUTH_FORGOT, data);
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    await api.post(ENDPOINTS.AUTH_RESET, data);
  },

  getProfile: async (): Promise<CustomerProfile> => {
    const response = await api.get<StoreProfileApiResponse>(ENDPOINTS.ACCOUNT_PROFILE);
    return mapProfile(response.data);
  },

  updateProfile: async (data: Partial<CustomerProfile>): Promise<CustomerProfile> => {
    const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
    const response = await api.put<StoreProfileApiResponse>(ENDPOINTS.ACCOUNT_PROFILE, {
      name: name || undefined,
      phone: data.phone,
    });
    return mapProfile(response.data);
  },
};

export default authenticationService;
